import React from "react";

/**
 * Word-level diff for prompt preview (removed = red strikethrough, added = green).
 */

function tokenize(text) {
  const s = text == null ? "" : String(text);
  if (!s) return [];
  return s.split(/(\s+)/).filter((t) => t.length > 0);
}

function lcsMatrix(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  return dp;
}

/**
 * @param {string} before
 * @param {string} after
 * @returns {Array<{ type: 'equal'|'removed'|'added', text: string }>}
 */
export function diffWords(before, after) {
  const a = tokenize(before);
  const b = tokenize(after);
  if (a.length + b.length > 8000) {
    // Fallback for very large prompts: treat as whole-block replace
    if (before === after) return [{ type: "equal", text: after || "" }];
    const parts = [];
    if (before) parts.push({ type: "removed", text: String(before) });
    if (after) parts.push({ type: "added", text: String(after) });
    return parts;
  }

  const dp = lcsMatrix(a, b);
  const parts = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      parts.push({ type: "equal", text: a[i] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      parts.push({ type: "removed", text: a[i] });
      i += 1;
    } else {
      parts.push({ type: "added", text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) {
    parts.push({ type: "removed", text: a[i] });
    i += 1;
  }
  while (j < b.length) {
    parts.push({ type: "added", text: b[j] });
    j += 1;
  }

  // Merge consecutive same-type parts
  const merged = [];
  for (const part of parts) {
    const last = merged[merged.length - 1];
    if (last && last.type === part.type) last.text += part.text;
    else merged.push({ ...part });
  }
  return merged;
}

export function DiffView({ before, after, className = "" }) {
  const parts = diffWords(before, after);
  return (
    <pre
      className={`whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-gray-800 ${className}`}
    >
      {parts.map((part, idx) => {
        if (part.type === "removed") {
          return (
            <span key={idx} className="bg-red-100 text-red-700 line-through">
              {part.text}
            </span>
          );
        }
        if (part.type === "added") {
          return (
            <span key={idx} className="bg-green-100 text-green-800">
              {part.text}
            </span>
          );
        }
        return <span key={idx}>{part.text}</span>;
      })}
    </pre>
  );
}
